import type {
  GetOrderParams,
  GetOrderRes,
  GetRocketDeliveryAvailableParams,
  GetRocketDeliveryAvailableRes,
  GetUsableCouponListRes,
  PatchUserDeliveryAddressPayload,
  PatchUserDeliveryAddressRes,
  PostMemberOrderEsimPayload,
  PostMemberOrderUsimPayload,
  PostGuestOrderEsimPayload,
  PostGuestOrderUsimPayload,
  PostOrderRes,
  GetOrderGroupCompleteRes,
  GetGuestOrderGroupCompleteRes,
  PostOrderApprovePayload,
  GetMemberExtendOrderRes,
  PostMemberOrderExtendPayload,
  GetMemberAddonOrderParams,
  GetMemberAddonOrderRes,
  PostMemberOrderAddonPayload,
  GetTossReceiptRes,
  PostRefundPayload,
  GetOrderDetailRes,
  GetBestPriceDataRes
} from '@/uTypes/order/api'
import type { ApiRes, ApiClientType, ApiBaseUrlType, HeadersOptionType } from '@/uTypes/api'

export const useOrderAPI = () => {
  const nuxtApp = useNuxtApp()
  const getApiClient = (
    nuxtApp.$api as {
      getApiClient: (
        apiBaseUrlType?: ApiBaseUrlType,
        headersOption?: HeadersOptionType
      ) => ApiClientType
    }
  ).getApiClient
  const $api = getApiClient()
  const $apiV2 = getApiClient('v2')
  const { getOrderTypeQuery } = useQuery()
  const { getPartnersKeyHeader } = useCouponScope()

  // 결제 상세 조회
  const getOrder = (params: GetOrderParams): Promise<GetOrderRes> => {
    return getApiClient('v1', getPartnersKeyHeader()).get('/orders/checkout', params)
  }

  // 결제 페이지 > 사용 가능한 쿠폰 목록 조회
  const getUsableCoupons = (params: GetOrderParams): Promise<GetUsableCouponListRes> => {
    return getApiClient('v1', getPartnersKeyHeader()).get('/orders/checkout/coupons', params)
  }

  // 결제 페이지 > 최대 할인 적용가 조회
  const getBestPriceData = (
    productId: string,
    params: {
      days: number
      simType: 'esim' | 'usim'
    }
  ): Promise<GetBestPriceDataRes> => {
    return $api.get(`/products/${productId}/best-coupons`, params)
  }

  // 연장 결제 페이지 > 사용 가능한 쿠폰 목록 조회
  const getExtendUsableCoupons = (params: GetOrderParams): Promise<GetUsableCouponListRes> => {
    return getApiClient('v1', getPartnersKeyHeader()).get(
      '/orders/checkout/extend/coupons',
      params
    )
  }

  // 회원 주문번호
  const postMemberOrder = (
    payload: PostMemberOrderEsimPayload | PostMemberOrderUsimPayload
  ): Promise<PostOrderRes> => {
    const affLinkId = useCookie<string | null>('affLinkId').value
    const headersOption: HeadersOptionType | undefined = affLinkId
      ? { 'x-aff-link-id': affLinkId }
      : undefined
    const apiWithHeader = getApiClient('v1', headersOption)
    return apiWithHeader.post('/orders/member', payload)
  }

  // [다건] 회원 다건 구매
  const postMemberMultiPrdOrder = (
    payload: PostMemberOrderEsimPayload | PostMemberOrderUsimPayload
  ): Promise<PostOrderRes> => {
    const affLinkId = useCookie<string | null>('affLinkId').value
    const headersOption: HeadersOptionType | undefined = affLinkId
      ? { 'x-aff-link-id': affLinkId }
      : undefined
    const apiWithHeader = getApiClient('v3', headersOption)
    return apiWithHeader.post('/orders/group', payload)
  }

  // 파트너스 회원 주문번호
  const postPartnersMemberOrder = (
    payload: PostMemberOrderEsimPayload | PostMemberOrderUsimPayload
  ): Promise<PostOrderRes> => {
    const partnersSubCode = useCookie<string | null>('partnersSubCode').value
    const headersOption: HeadersOptionType = {
      ...(partnersSubCode ? { 'x-sub-channel-code': partnersSubCode } : {}),
      ...getPartnersKeyHeader()
    }
    const apiWithHeader = getApiClient('v1', headersOption)
    return apiWithHeader.post('/orders/member', payload)
  }

  // 회원 주문 승인
  const postMemberOrderApprove = (payload: PostOrderApprovePayload): Promise<ApiRes> => {
    return $api.post('/orders/member/approve', payload)
  }

  // [다건] 회원 다건 구매 승인
  const postMemberMultiPrdOrderApprove = (payload: PostOrderApprovePayload): Promise<ApiRes> => {
    const $apiV3 = getApiClient('v3')
    return $apiV3.post('/orders/group/approve', payload)
  }

  // [다건] 회원 주문 완료 조회
  const getMemberOrderGroupComplete = (orderGroupId: string): Promise<GetOrderGroupCompleteRes> => {
    const $apiV3 = getApiClient('v3')
    return $apiV3.get(`/orders/group/complete/${orderGroupId}`)
  }

  // [다건] 비회원 주문 완료 조회
  const getGuestOrderGroupComplete = (
    orderGroupId: string
  ): Promise<GetGuestOrderGroupCompleteRes> => {
    const $apiV3 = getApiClient('v3')
    return $apiV3.get(`/orders/guest/complete/${orderGroupId}`)
  }

  // 연장 결제 상세 조회
  const getMemberExtendOrder = (params: GetOrderParams): Promise<GetMemberExtendOrderRes> => {
    return getApiClient('v1', getPartnersKeyHeader()).get('/orders/checkout/extend', params)
  }

  // 연장 주문번호
  const postMemberExtendOrder = (payload: PostMemberOrderExtendPayload): Promise<PostOrderRes> => {
    return getApiClient('v1', getPartnersKeyHeader()).post('/orders/member/extend', payload)
  }

  // [다건] 연장
  const postMemberExtendOrderGroup = (
    payload: PostMemberOrderExtendPayload
  ): Promise<PostOrderRes> => {
    const $apiV3 = getApiClient('v3')
    return $apiV3.post('/orders/group/extend', payload)
  }

  // 충전 결제 상세 조회
  const getMemberAddonOrder = (
    params: GetMemberAddonOrderParams
  ): Promise<GetMemberAddonOrderRes> => {
    return $api.get('/orders/checkout/addon', params)
  }

  // 충전 주문번호
  const postMemberAddonOrder = (payload: PostMemberOrderAddonPayload): Promise<PostOrderRes> => {
    return $api.post('/orders/member/addon', payload)
  }
  const postMemberAddonOrderGroup = (
    payload: PostMemberOrderAddonPayload
  ): Promise<PostOrderRes> => {
    const $apiV3 = getApiClient('v3')
    return $apiV3.post('/orders/group/recharge', payload)
  }

  // 비회원 주문 약관 조회
  const getGuestOrderTerm = () => {
    return $api.get('/orders/guest/terms')
  }

  // 비회원 주문번호
  const postGuestOrder = (
    payload: PostGuestOrderEsimPayload | PostGuestOrderUsimPayload
  ): Promise<PostOrderRes> => {
    const affLinkId = useCookie<string | null>('affLinkId').value
    const partnersSubCode = useCookie<string | null>('partnersSubCode').value
    const headersOption: HeadersOptionType | undefined =
      affLinkId || partnersSubCode
        ? {
            ...(affLinkId ? { 'x-aff-link-id': affLinkId } : {}),
            ...(partnersSubCode ? { 'x-sub-channel-code': partnersSubCode } : {})
          }
        : undefined
    const apiWithHeader = getApiClient('v1', headersOption)
    return apiWithHeader.post('/orders/guest', payload)
  }

  //[다건] 비회원 다건 결제 (다건에서 1건 주문)
  const postGuestMultiPrdOrder = (
    payload: PostGuestOrderEsimPayload | PostGuestOrderUsimPayload
  ): Promise<PostOrderRes> => {
    const affLinkId = useCookie<string | null>('affLinkId').value
    const headersOption: HeadersOptionType | undefined = affLinkId
      ? { 'x-aff-link-id': affLinkId }
      : undefined
    const apiWithHeader = getApiClient('v3', headersOption)
    return apiWithHeader.post('/orders/guest', payload)
  }

  // 비회원 주문 승인
  const postGuestOrderApprove = (payload: PostOrderApprovePayload): Promise<ApiRes> => {
    return $api.post('/orders/guest/approve', payload)
  }

  // [다건] 비회원 주문 승인
  const postGuestMultiPrdOrderApprove = (payload: PostOrderApprovePayload): Promise<ApiRes> => {
    const $apiV3 = getApiClient('v3')
    return $apiV3.post('/orders/guest/approve', payload)
  }

  // 당일 배송 가능 여부 조회(두발히어로 API)
  const getRocketDeliveryAvailable = (
    payload: GetRocketDeliveryAvailableParams
  ): Promise<ApiRes<GetRocketDeliveryAvailableRes>> => {
    return $api.post('/delivery/dhero/validate-address', payload)
  }

  // 회원 배송지 정보 변경
  const patchUserDeliveryAddress = (
    payload: PatchUserDeliveryAddressPayload
  ): Promise<PatchUserDeliveryAddressRes> => {
    return $api.patch('/users/delivery/address', payload)
  }

  // 주문 상세
  const getOrderDetail = (orderId: string): Promise<ApiRes<GetOrderDetailRes>> => {
    return $api.get(`/users/orders/${orderId}`)
  }
  // [다건] 주문 상세
  const getMultiOrderDetail = (
    orderId: string,
    tpye: 'group' | 'order'
  ): Promise<ApiRes<GetOrderDetailRes>> => {
    const $apiV3 = getApiClient('v3')
    return $apiV3.get(`/users/orders/${orderId}${getOrderTypeQuery(tpye)}`)
  }

  // [다건] 충전 연장 주문 상세
  const getMultiOrderSimHistory = (iccid: string): Promise<ApiRes<GetOrderDetailRes>> => {
    const $apiV3 = getApiClient('v3')
    return $apiV3.get(`/users/orders/sim-history/${iccid}`)
  }

  // 환불 요청
  const postRefundOrder = (params: PostRefundPayload): Promise<ApiRes> => {
    return $api.post('/orders/cancel', params)
  }

  //[다건] 환불 요청
  const postMultiAllPrdRefundOrder = (params: PostRefundPayload): Promise<ApiRes> => {
    const $apiV3 = getApiClient('v3')
    return $apiV3.post('/orders/cancel', params)
  }

  // 토스 영수증 조회
  const getTossReceipt = (paymentKey: string): Promise<GetTossReceiptRes> => {
    return $api.get(`/payments/${paymentKey}/receipt/`)
  }

  return {
    getOrder,
    getUsableCoupons,
    getBestPriceData,
    getExtendUsableCoupons,
    postMemberOrder,
    postMemberMultiPrdOrder,
    postPartnersMemberOrder,
    getGuestOrderTerm,
    postGuestOrder,
    postGuestMultiPrdOrder,
    postMemberOrderApprove,
    postMemberMultiPrdOrderApprove,
    getMemberOrderGroupComplete,
    getGuestOrderGroupComplete,
    postGuestOrderApprove,
    postGuestMultiPrdOrderApprove,
    getMemberExtendOrder,
    postMemberExtendOrder,
    postMemberExtendOrderGroup,
    getMemberAddonOrder,
    postMemberAddonOrder,
    postMemberAddonOrderGroup,
    getRocketDeliveryAvailable,
    patchUserDeliveryAddress,
    getOrderDetail,
    getMultiOrderDetail,
    getMultiOrderSimHistory,
    postRefundOrder,
    postMultiAllPrdRefundOrder,
    getTossReceipt
  }
}
